import {
  useState, useMemo, useEffect, useCallback,
} from 'react';
import {
  LancamentoSemente,
  EstatisticasRepeticao,
  FormValues,
  GrauConfig,
} from '../components/FormularioTetrazolio/FormularioTetrazolioClassificacao/types';
import {
  TipoDano,
  TipoExigenciaDano,
  IntensidadeDano,
} from './useTetrazolioConfiguracoes';
import { LancamentoSementeAPI } from '../../../../services/sementes/tetrazolio';

const mapStringToTipoDano = (tipo: string): TipoDano => {
  const tipoLower = tipo.toLowerCase();
  const enumValue = Object.values(TipoDano).find((v) => v === tipoLower);
  if (enumValue) return enumValue as TipoDano;

  const mapa: Record<string, TipoDano> = {
    duras: TipoDano.DURAS,
    esverdeadas: TipoDano.ESVERDEADAS,
    dano_mecanico_latente: TipoDano.DANO_MECANICO_LATENTE,
    rasgo_no_tegumento: TipoDano.RASGO_NO_TEGUMENTO,
  };
  // Não deve acontecer, mas se não encontrar, retorna MECANICO como padrão para evitar
  // quebra do frontend
  return mapa[tipoLower] || TipoDano.MECANICO;
};

interface UseClassificacaoSementesResult {
  lancamentos: LancamentoSemente[];
  todosLancamentos: LancamentoSemente[];
  estatisticas: EstatisticasRepeticao;
  repeticaoAtual: number;
  totalRepeticoes: number;
  sementesPorRepeticao: number;
  podeAvancarRepeticao: boolean;
  podeConcluirAnalise: boolean;
  adicionarLancamento: (values: FormValues) => void;
  removerLancamento: (id: string) => void;
  proximaRepeticao: () => void;
  irParaRepeticao: (numeroRepeticao: number) => void;
  validarLancamento: (values: FormValues) => string | null;
  resetarClassificacao: () => void;
  rejeitarRepeticao: (numeroRepeticao: number, criarNova: boolean) => void;
  reativarRepeticao: (numeroRepeticao: number) => boolean;
  repeticaoEstaRejeitada: boolean;
  repeticaoEstaCompleta: boolean;
  repeticaoRejeitada: number | null;
  totalRepeticoesAtivas: number;
  pendingRejeitarRepeticao: { numeroRepeticao: number; repeticaoJaRejeitada: number } | null;
  confirmarTrocaRepeticaoRejeitada: () => void;
  cancelarTrocaRepeticaoRejeitada: () => void;
  getLancamentosPorRepeticaoParaAPI: (
    totalRepeticoesOverride?: number
  ) => Record<number, LancamentoSementeAPI[]>;
  temDadosNaoSincronizados: boolean;
  marcarComoSincronizado: () => void;
}

interface DadosAnaliseInicial {
  repeticoes: number;
  sementesPorRepeticao: number;
  repeticaoRejeitada?: number | null;
}

function useClassificacaoSementes(
  dadosAnaliseInicial?: DadosAnaliseInicial | null,
  graus: GrauConfig[] = [],
  lancamentosExistentes?: Record<number, LancamentoSementeAPI[]>,
): UseClassificacaoSementesResult {
  const [lancamentosPorRepeticao, setLancamentosPorRepeticao] = useState<
    Record<number, LancamentoSemente[]>
  >({});
  const [repeticaoAtual, setRepeticaoAtual] = useState(1);
  const [repeticaoRejeitada, setRepeticaoRejeitada] = useState<number | null>(
    dadosAnaliseInicial?.repeticaoRejeitada ?? null,
  );
  const [temDadosNaoSincronizados, setTemDadosNaoSincronizados] = useState(false);

  const calcularRepeticoes = useCallback(() => {
    if (!dadosAnaliseInicial) {
      return { iniciais: 0, adicionais: 0 };
    }

    const totalNaAPI = dadosAnaliseInicial.repeticoes;
    const temRepeticaoRejeitada = dadosAnaliseInicial.repeticaoRejeitada !== null
      && dadosAnaliseInicial.repeticaoRejeitada !== undefined;

    if (temRepeticaoRejeitada) {
      const iniciais = totalNaAPI <= 3 ? 2 : 4;
      // Sempre ter pelo menos 1 adicional quando houver rejeição
      // Se a API já incrementou (totalNaAPI > iniciais), usar esse valor
      // Senão, adicionar 1 para criar a aba da repetição adicional
      const adicionais = Math.max(1, totalNaAPI - iniciais);
      return { iniciais, adicionais };
    }

    return { iniciais: totalNaAPI, adicionais: 0 };
  }, [dadosAnaliseInicial]);

  const {
    iniciais: repeticoesIniciaisCalculadas,
    adicionais: repeticoesAdicionaisCalculadas,
  } = useMemo(
    () => calcularRepeticoes(), [calcularRepeticoes],
  );

  const [repeticoesAdicionais, setRepeticoesAdicionais] = useState(
    repeticoesAdicionaisCalculadas,
  );
  const [pendingRejeitarRepeticao, setPendingRejeitarRepeticao] = useState<{
    numeroRepeticao: number;
    repeticaoJaRejeitada: number;
  } | null>(null);

  useEffect(() => {
    if (!dadosAnaliseInicial) {
      return;
    }
    const novaRepeticaoRejeitada = dadosAnaliseInicial.repeticaoRejeitada ?? null;
    setRepeticaoRejeitada(novaRepeticaoRejeitada);

    const { adicionais } = calcularRepeticoes();
    setRepeticoesAdicionais(adicionais);
  }, [dadosAnaliseInicial]);

  useEffect(() => {
    if (!lancamentosExistentes || Object.keys(lancamentosExistentes).length <= 0) {
      return;
    }
    const lancamentosConvertidos: Record<number, LancamentoSemente[]> = {};

    Object.entries(lancamentosExistentes).forEach(([repeticao, lancamentos]) => {
      lancamentosConvertidos[Number(repeticao)] = lancamentos.map((lanc, index) => {
        // lanc.grau agora é uma string (ID do grau)
        const grauId = lanc.grau;

        // Encontrar o grauConfig correspondente pelo ID
        const grauConfig = graus.find((grau) => grau.id === grauId);

        return {
          id: `${repeticao}-${index}`,
          contagem: lanc.contagem,
          grau: grauConfig?.grau || 1,
          grauId,
          danos: lanc.danos.map((dano) => ({
            tipo: mapStringToTipoDano(dano.tipo),
            intensidade: dano.intensidade as IntensidadeDano,
          })),
          timestamp: new Date(),
          repeticao: Number(repeticao),
        };
      });
    });

    // Se houver uma repetição rejeitada, garantir que existe a repetição adicional vazia
    if (dadosAnaliseInicial?.repeticaoRejeitada) {
      const repeticoesIniciais = dadosAnaliseInicial.repeticoes <= 3 ? 2 : 4;
      const numeroRepeticaoAdicional = repeticoesIniciais + 1;

      // Se a repetição adicional não existe nos lançamentos, criar vazia
      if (!lancamentosConvertidos[numeroRepeticaoAdicional]) {
        lancamentosConvertidos[numeroRepeticaoAdicional] = [];
      }
    }

    setLancamentosPorRepeticao(lancamentosConvertidos);
  }, [lancamentosExistentes, graus, dadosAnaliseInicial]);

  const repeticoesIniciais = repeticoesIniciaisCalculadas;
  const totalRepeticoes = repeticoesIniciais + repeticoesAdicionais;
  const sementesPorRepeticao = dadosAnaliseInicial?.sementesPorRepeticao || 50;

  const lancamentosRepeticaoAtual = useMemo(
    () => lancamentosPorRepeticao[repeticaoAtual] || [],
    [lancamentosPorRepeticao, repeticaoAtual],
  );

  const todosLancamentos = useMemo(
    () => Object.values(lancamentosPorRepeticao).flat(),
    [lancamentosPorRepeticao],
  );

  const estatisticas = useMemo((): EstatisticasRepeticao => {
    if (sementesPorRepeticao === 0) {
      return {
        vigor: 0,
        viabilidade: 0,
        sementesLancadas: 0,
        totalSementes: 0,
      };
    }

    const sementesLancadas = lancamentosRepeticaoAtual.length;

    let somaVigor = 0;
    let somaViabilidade = 0;

    lancamentosRepeticaoAtual.forEach((lancamento) => {
      if (lancamento.grau >= 1 && lancamento.grau <= 3) {
        somaVigor += 1;
      }
      if (lancamento.grau >= 1 && lancamento.grau <= 5) {
        somaViabilidade += 1;
      }
    });

    const vigor = sementesLancadas > 0
      ? Math.round((somaVigor / sementesPorRepeticao) * 100)
      : 0;
    const viabilidade = sementesLancadas > 0
      ? Math.round((somaViabilidade / sementesPorRepeticao) * 100)
      : 0;

    return {
      vigor,
      viabilidade,
      sementesLancadas,
      totalSementes: sementesPorRepeticao,
    };
  }, [lancamentosRepeticaoAtual, sementesPorRepeticao]);

  const podeAvancarRepeticao = useMemo(() => {
    const repeticaoAtualCompleta = estatisticas.sementesLancadas >= sementesPorRepeticao;
    if (!repeticaoAtualCompleta) return false;

    const repeticoesAtivas = Array.from({ length: totalRepeticoes }, (_, i) => i + 1)
      .filter((num) => num !== repeticaoRejeitada);

    const proximaRepeticaoAtiva = repeticoesAtivas.find((num) => num > repeticaoAtual);
    return !!proximaRepeticaoAtiva;
  }, [
    estatisticas.sementesLancadas,
    sementesPorRepeticao,
    repeticaoAtual,
    totalRepeticoes,
    repeticaoRejeitada,
  ]);

  const podeConcluirAnalise = useMemo(() => {
    const repeticoesAtivas = Array.from({ length: totalRepeticoes }, (_, i) => i + 1)
      .filter((num) => num !== repeticaoRejeitada);

    const todasRepeticoesCompletas = repeticoesAtivas.every((num) => {
      const lancamentosRep = lancamentosPorRepeticao[num] || [];
      return lancamentosRep.length >= sementesPorRepeticao;
    });

    return todasRepeticoesCompletas && repeticoesAtivas.length > 0;
  }, [
    totalRepeticoes,
    repeticaoRejeitada,
    lancamentosPorRepeticao,
    sementesPorRepeticao,
  ]);

  const validarLancamento = (
    values: FormValues,
    grausConfig: GrauConfig[],
  ): string | null => {
    if (repeticaoEstaRejeitada) {
      return 'Não é possível lançar em uma repetição rejeitada';
    }

    if (values.grauSelecionado === null || values.grauSelecionado === undefined) {
      return 'Selecione um grau';
    }

    const grauConfig = grausConfig.find(
      (g) => g.grau === values.grauSelecionado,
    );
    if (!grauConfig) return 'Grau inválido';

    if (
      grauConfig.exigenciaDano === TipoExigenciaDano.NORMAL
      && values.danosSelecionados.length === 0
    ) {
      return 'Este grau requer pelo menos um dano';
    }

    if (grauConfig.exigenciaDano === TipoExigenciaDano.INTENSO) {
      const temDanoIntenso = values.danosSelecionados.some(
        (d) => d.intensidade === IntensidadeDano.INTENSO,
      );
      if (!temDanoIntenso) {
        return 'Este grau requer pelo menos um dano intenso';
      }
    }

    if (estatisticas.sementesLancadas >= sementesPorRepeticao) {
      return 'Já foram lançadas todas as sementes desta repetição';
    }

    return null;
  };

  const adicionarLancamento = (values: FormValues) => {
    const erro = validarLancamento(values, graus);
    if (erro) {
      throw new Error(erro);
    }

    if (!values.grauIdSelecionado) {
      throw new Error('ID do grau não encontrado');
    }

    setLancamentosPorRepeticao((prev) => {
      const lancamentosAtuais = prev[repeticaoAtual] || [];
      const maxContagem = lancamentosAtuais.length > 0
        ? Math.max(...lancamentosAtuais.map((l) => l.contagem))
        : 0;

      const novoLancamento: LancamentoSemente = {
        id: Date.now().toString() + Math.random().toString(36),
        contagem: maxContagem + 1,
        grau: values.grauSelecionado!,
        grauId: values.grauIdSelecionado!,
        danos: values.danosSelecionados,
        timestamp: new Date(),
        repeticao: repeticaoAtual,
      };

      return {
        ...prev,
        [repeticaoAtual]: [novoLancamento, ...lancamentosAtuais],
      };
    });
    setTemDadosNaoSincronizados(true);
  };

  const removerLancamento = useCallback((id: string) => {
    setLancamentosPorRepeticao((prev) => ({
      ...prev,
      [repeticaoAtual]: (prev[repeticaoAtual] || []).filter((l) => l.id !== id),
    }));
    setTemDadosNaoSincronizados(true);
  }, [repeticaoAtual]);

  const proximaRepeticao = () => {
    if (podeAvancarRepeticao) {
      setRepeticaoAtual((prev) => prev + 1);
    }
  };

  const irParaRepeticao = (numeroRepeticao: number) => {
    if (numeroRepeticao >= 1 && numeroRepeticao <= totalRepeticoes) {
      setRepeticaoAtual(numeroRepeticao);
    }
  };

  const resetarClassificacao = () => {
    setLancamentosPorRepeticao({});
    setRepeticaoAtual(1);
    setRepeticaoRejeitada(null);
    setRepeticoesAdicionais(0);
  };

  const repeticaoEstaRejeitada = useMemo(
    () => repeticaoRejeitada === repeticaoAtual,
    [repeticaoRejeitada, repeticaoAtual],
  );

  const repeticaoEstaCompleta = useMemo(
    () => estatisticas.sementesLancadas >= sementesPorRepeticao,
    [estatisticas.sementesLancadas, sementesPorRepeticao],
  );

  const totalRepeticoesAtivas = useMemo(
    () => totalRepeticoes - (repeticaoRejeitada !== null ? 1 : 0),
    [totalRepeticoes, repeticaoRejeitada],
  );

  const rejeitarRepeticao = (numeroRepeticao: number, criarNova: boolean) => {
    if (repeticaoRejeitada !== null && repeticaoRejeitada !== numeroRepeticao) {
      setPendingRejeitarRepeticao({
        numeroRepeticao,
        repeticaoJaRejeitada: repeticaoRejeitada,
      });
      return;
    }

    setRepeticaoRejeitada(numeroRepeticao);

    if (criarNova) {
      setRepeticoesAdicionais((prev) => prev + 1);
      setRepeticaoAtual(totalRepeticoes + 1);
    }
  };

  const confirmarTrocaRepeticaoRejeitada = () => {
    if (pendingRejeitarRepeticao) {
      setRepeticaoRejeitada(pendingRejeitarRepeticao.numeroRepeticao);
      setPendingRejeitarRepeticao(null);
    }
  };

  const cancelarTrocaRepeticaoRejeitada = () => {
    setPendingRejeitarRepeticao(null);
  };

  const reativarRepeticao = (numeroRepeticao: number): boolean => {
    if (repeticaoRejeitada !== numeroRepeticao) {
      return false;
    }

    const totalAtivasAposReativar = totalRepeticoes;
    if (totalAtivasAposReativar > repeticoesIniciais) {
      return false;
    }

    setRepeticaoRejeitada(null);
    return true;
  };

  const getLancamentosPorRepeticaoParaAPI = useCallback((
    totalRepeticoesOverride?: number,
  ): Record<number, LancamentoSementeAPI[]> => {
    const resultado: Record<number, LancamentoSementeAPI[]> = {};
    const totalParaUsar = totalRepeticoesOverride ?? totalRepeticoes;

    for (let i = 1; i <= totalParaUsar; i += 1) {
      const lancamentos = lancamentosPorRepeticao[i] || [];

      resultado[i] = lancamentos.map((lanc) => ({
        contagem: lanc.contagem,
        grau: lanc.grauId,
        danos: lanc.danos.map((dano) => ({
          tipo: dano.tipo,
          intensidade: dano.intensidade,
        })),
      }));
    }

    return resultado;
  }, [lancamentosPorRepeticao, totalRepeticoes]);

  const marcarComoSincronizado = () => {
    setTemDadosNaoSincronizados(false);
  };

  return {
    lancamentos: lancamentosRepeticaoAtual,
    todosLancamentos,
    estatisticas,
    repeticaoAtual,
    totalRepeticoes,
    sementesPorRepeticao,
    podeAvancarRepeticao,
    podeConcluirAnalise,
    adicionarLancamento,
    removerLancamento,
    proximaRepeticao,
    irParaRepeticao,
    validarLancamento: (values: FormValues) => validarLancamento(values, graus),
    resetarClassificacao,
    rejeitarRepeticao,
    reativarRepeticao,
    repeticaoEstaRejeitada,
    repeticaoEstaCompleta,
    repeticaoRejeitada,
    totalRepeticoesAtivas,
    pendingRejeitarRepeticao,
    confirmarTrocaRepeticaoRejeitada,
    cancelarTrocaRepeticaoRejeitada,
    getLancamentosPorRepeticaoParaAPI,
    temDadosNaoSincronizados,
    marcarComoSincronizado,
  };
}

export default useClassificacaoSementes;
